import React, { forwardRef, HTMLAttributes } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { typographyStyles } from "../common/Typography";
import { ScrollbarStyles } from "../styles/scrollbar.styles";
import { byDisplayType, IDisplayTypeProps } from "./IDisplayTypeProps";
import { useDisplayType } from "./PickerDisplayContext";

const StyledContent = styled.div<IDisplayTypeProps>(
    props => ({
        backgroundColor:
            props.displayType === "popover"
                ? "var(--color-background-secondary)"
                : "var(--color-background-surface)",
    }),
    {
        flexGrow: 1,
        overflowX: "hidden",
        overflowY: "auto",
    },
    ScrollbarStyles,
    props => css`
        &::before {
            content: "";
            display: block;
            position: sticky;
            top: 0;
            left: 0;
            right: 0;
            width: 100%;
            height: 0.5rem;
        }

        &::before {
            background-image: linear-gradient(
                0deg,
                transparent,
                ${props.displayType === "popover"
                    ? "var(--color-background-secondary)"
                    : "var(--color-background-surface)"}
            );
            top: 0;
        }
    `,
);

export function Content(props: HTMLAttributes<HTMLElement>) {
    return <StyledContent displayType={useDisplayType()} {...props} />;
}

const GroupedSpacing = styled.div`
    display: flex;
    flex-direction: column;

    gap: 1rem;
    margin-block-end: 1rem;

    &:empty::after {
        content: "No results found…";
        padding-inline: ${24 / 16}rem;
        ${typographyStyles.list};
        color: var(--color-text-primary);
        font-style: italic;
        opacity: 0.6;
    }
`;

export function ContentContainer(props: HTMLAttributes<HTMLElement>) {
    const { children, ...rest } = props;
    return (
        <Content {...rest}>
            <GroupedSpacing>{children}</GroupedSpacing>
        </Content>
    );
}

const StyledContainer = styled.main<IDisplayTypeProps>(
    {
        borderRadius: `${6 / 16}rem`,
        color: "var(--color-text-secondary)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
        userSelect: "none",
    },
    props => ({
        width: byDisplayType({
            popover: `${400 / 16}rem`,
            dropdown: undefined,
            small: undefined,
        })(props),
        outlineStyle: "solid",
        outlineWidth: byDisplayType({
            popover: "0",
            dropdown: "1px",
            small: "1px",
        })(props),
        outlineColor: byDisplayType({
            popover: "none",
            dropdown: "var(--color-border-active)",
            small: "var(--color-border-active)",
        })(props),
        boxShadow: byDisplayType({
            popover: `0 ${10 / 16}rem ${10 / 16}rem rgba(0, 0, 0, 0.25)`,
            dropdown: "none",
            small: "none",
        })(props),
    }),
);

export const Container = forwardRef<HTMLElement, HTMLAttributes<HTMLElement>>(
    function Container(props, ref) {
        return (
            <StyledContainer
                displayType={useDisplayType()}
                {...props}
                ref={ref}
            />
        );
    },
);

const StyledHeader = styled.header<IDisplayTypeProps>(props => ({
    backgroundColor: byDisplayType({
        popover: "var(--color-background-primary)",
        dropdown: "var(--color-background-primary)",
        small: "var(--color-background-primary)",
    })(props),
}));

export const BasicHeader = styled.header`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding-inline: 2rem;
    padding-block: 1rem;
    background: var(--color-background-surface);
`;

export function Header(props: HTMLAttributes<HTMLElement>) {
    return <StyledHeader displayType={useDisplayType()} {...props} />;
}
